import React, { useEffect, useState } from "react";
import { ShieldAlert, X, RefreshCw } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "sonner";

/**
 * KommoCircuitBanner — banner top-level que aparece SÓ quando o circuit breaker
 * do Kommo está OPEN (i.e., Kommo está a bloquear com WAF/403 persistente).
 *
 * Poll: a cada 45s. Silencia falhas de rede (não spamma).
 *
 * Admin vê botão "Reset manual" quando quiser forçar reactivação.
 */
export default function KommoCircuitBanner() {
    const { user } = useAuth();
    const [state, setState] = useState(null);
    const [killSwitch, setKillSwitch] = useState(null);
    const [dismissed, setDismissed] = useState(false);
    const [resetting, setResetting] = useState(false);

    const load = async () => {
        try {
            const [c, k] = await Promise.allSettled([
                api.get("/kommo/circuit-state"),
                api.get("/kommo/kill-switch"),
            ]);
            if (c.status === "fulfilled") setState(c.value.data);
            if (k.status === "fulfilled") setKillSwitch(k.value.data);
        } catch { /* silencioso */ }
    };

    useEffect(() => {
        load();
        const id = setInterval(load, 45_000);
        return () => clearInterval(id);
    }, []);

    // Reset dismiss quando o estado muda de closed→open ou kill-switch on
    useEffect(() => {
        if (state?.is_open || killSwitch?.active) setDismissed(false);
    }, [state?.is_open, killSwitch?.active]);

    // Prioridade: kill switch > circuit breaker
    if (killSwitch?.active && !dismissed) {
        return (
            <div
                className="border-b-2 border-rose-600 bg-rose-100 px-4 py-2 flex items-center gap-3 text-rose-900 text-[13px]"
                data-testid="kommo-kill-switch-banner"
            >
                <ShieldAlert size={18} className="shrink-0" />
                <div className="flex-1 min-w-0">
                    <div className="font-bold leading-tight uppercase">
                        🛑 Kommo pausado manualmente
                    </div>
                    <div className="text-[11px] leading-tight opacity-80">
                        Sincronização automática bloqueada. Vendas continuam a ser gravadas. Admin desliga em /estado-sistema.
                    </div>
                </div>
                {user?.role === "admin" && (
                    <a
                        href="/estado-sistema"
                        className="h-7 px-2 border border-rose-700 rounded text-[11px] font-bold hover:bg-rose-200 flex items-center gap-1 shrink-0"
                        data-testid="kommo-kill-switch-goto"
                    >
                        Ir a Estado do Sistema
                    </a>
                )}
                <button
                    onClick={() => setDismissed(true)}
                    className="p-1 hover:bg-rose-200 rounded shrink-0"
                    title="Ocultar (reaparece a cada 45s)"
                    data-testid="kommo-kill-switch-dismiss"
                >
                    <X size={14} />
                </button>
            </div>
        );
    }

    if (!state || !state.is_open || dismissed) return null;

    const cd = state.cooldown_until;
    const cdLabel = cd ? new Date(cd).toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit" }) : null;

    const doReset = async () => {
        setResetting(true);
        try {
            await api.post("/kommo/circuit-reset");
            toast.success("Circuit breaker resetado — próxima venda vai tentar chamar Kommo");
            await load();
        } catch (e) { toast.error(apiError(e)); } finally { setResetting(false); }
    };

    return (
        <div
            className="border-b-2 border-amber-400 bg-amber-50 px-4 py-2 flex items-center gap-3 text-amber-900 text-[13px]"
            data-testid="kommo-circuit-banner"
        >
            <ShieldAlert size={18} className="shrink-0" />
            <div className="flex-1 min-w-0">
                <div className="font-bold leading-tight">
                    Kommo bloqueado temporariamente (WAF).
                </div>
                <div className="text-[11px] leading-tight opacity-80">
                    Vendas continuam a ser gravadas normalmente. Sincronização automática vai retomar {cdLabel ? `às ${cdLabel}` : "assim que o Kommo desbloquear"}.
                </div>
            </div>
            {user?.role === "admin" && (
                <button
                    onClick={doReset}
                    disabled={resetting}
                    className="h-7 px-2 border border-amber-600 rounded text-[11px] font-bold hover:bg-amber-100 disabled:opacity-50 flex items-center gap-1 shrink-0"
                    data-testid="kommo-circuit-reset"
                >
                    <RefreshCw size={11} className={resetting ? "animate-spin" : ""} />
                    Tentar agora
                </button>
            )}
            <button
                onClick={() => setDismissed(true)}
                className="p-1 hover:bg-amber-100 rounded shrink-0"
                title="Ocultar até próxima abertura"
                data-testid="kommo-circuit-dismiss"
            >
                <X size={14} />
            </button>
        </div>
    );
}
